import LazyImage from '../../LazyImage';
import { formattedPrice } from '../../Cart/util';
import { formatCategory } from '../../../util';
import { getProductImageUrl } from '../../../utils/menu/productUtils';
import StrainIcon from '../StrainIcon';
import VariantSelect from '../VariantSelect';
import VariantBox from '../VariantBox';
import SpecialOffer from '../SpecialOffer';
import usePricingType from '../../../hooks/usePricingType.js';
import { useConfig } from "../../../contexts/ConfigContext";
import { useAppState } from '../../../contexts/AppStateContext';
import EnablePopupWrapper from './EnablePopupWrapper.jsx';

const ProductCardv1 = ({
    product,
    prioritySpecialObject,
    allSpecials = null,
    showMultipleSpecials = false,
    singleProductUrl,
    selectedVariant,
    setSelectedVariant,
    handleAddItem,
    variables,
    errors,
    columns,
    menuSlug,
    variantTheme,
    handleVariantBoxSelection,
    handleVariantSelection,
    isFirstProduct = false,
}) => {
    const { retailerId } = useAppState();
    const { styleOptions } = useConfig();

    const [pricingType] = usePricingType(retailerId);
    const hasMultipleVariants = product?.variants?.length > 1;

    // This logic determines if the main price/add-to-cart button should render
    // for the whole card, typically when variants are handled by a dropdown or
    // if there's only one variant and boxes are used.
    const shouldRenderPrimaryAddToCart =
        !variantTheme ||
        variantTheme.toLowerCase() === 'dropdown' ||
        (!hasMultipleVariants && (variantTheme.toLowerCase() === 'boxes' || variantTheme.toLowerCase() === 'boxes-slider'));


    if (!product?.variants?.length || !selectedVariant || !pricingType) {
        return <div className={`headless-product-card ${columns ? `product-card-cols-${columns}` : ''}`}>Loading...</div>;
    }


    return (
        <div
            className={`headless-product-card ${columns ? `product-card-cols-${columns}` : ''}`}
            data-product-id={product.id}
            role="article"
            aria-label={`${product.name} product details`}
        >
            {errors && errors.length > 0 && // Check if errors is defined
                errors.map((error, i) => (
                    <div className='headless-error-message' role="alert" key={i}>
                        <code>{typeof error === 'object' ? JSON.stringify(error) : error}</code>
                    </div>
                ))}

            {/* Render specials - either multiple or single */}
            <SpecialOffer
                specialData={prioritySpecialObject}
                allSpecials={allSpecials}
                multiSpecial={showMultipleSpecials}
                menuSlug={menuSlug}
            />

            <EnablePopupWrapper
                product={product}
                productUrl={singleProductUrl}
                classNames="headless-card-image-link"
            >
                <LazyImage
                    classnames='headless-product-image'
                    src={`${getProductImageUrl(product)}?w=250&fm=webp`}
                    alt={`${product.name} product image`}
                    fetchPriority={isFirstProduct ? "high" : undefined}
                />
            </EnablePopupWrapper>

            {product.brand ? (
                <div className='headless-product-brands' aria-label="Brand">
                    {product.brand.name}
                </div>
            ) : null}

            <div className='headless-product-title-wrapper' role="presentation">
                <h3 className='headless-product-title'>
                    <EnablePopupWrapper
                        product={product}
                        productUrl={singleProductUrl}
                    >
                        <span>{product.name}</span>
                    </EnablePopupWrapper>
                </h3>
            </div>

            <div className='headless-product-bottom'>
                <hr className='headless-hr' aria-hidden="true" />

                <div className='headless-product-categories-wrapper'>
                    <div className='headless-product-categories' aria-label="Product category">
                        {product.category ? formatCategory(product.category) : ''}
                    </div>
                    {product.potencyThc?.formatted ? (
                        <div className='headless-product-card-potency' aria-label="THC content">
                            THC {product.potencyThc.formatted}
                        </div>
                    ) : null}
                    {product.strainType ? (
                        <div className='headless-product-strain' aria-label={`Strain type: ${product.strainType}`}>
                            <StrainIcon strainType={product.strainType} />
                        </div>
                    ) : null}
                </div>

                <hr className='headless-hr' aria-hidden="true" />

                {hasMultipleVariants && variantTheme && (
                    variantTheme.toLowerCase() === 'boxes' || variantTheme.toLowerCase() === 'boxes-slider' ? (
                        <VariantBox
                            variants={product.variants}
                            // setSelectedVariant={setSelectedVariant} // VariantBox might call handleVariantBoxSelection directly
                            // handleAddItem={handleAddItem} // VariantBox calls handleVariantBoxSelection
                            pricingType={pricingType}
                            // variables={variables} // VariantBox might construct its own for handleVariantBoxSelection
                            handleVariantBoxSelection={handleVariantBoxSelection}
                            boxType='card'
                            theme={variantTheme}
                            selectedVariantProp={selectedVariant} // Pass to highlight active
                        />
                    ) : variantTheme.toLowerCase() === 'dropdown' ? ( // Explicitly check for dropdown
                        <VariantSelect
                            product={product}
                            handleVariantSelection={handleVariantSelection}
                            selectedOptionId={selectedVariant?.id} // For default selection
                        />
                    ) : null
                )}

                {product.variants.length === 1 && selectedVariant.option && selectedVariant.option !== 'N/A' && (
                    <div className='headless-product-card-weight' aria-label="Product weight">
                        {selectedVariant.option}
                    </div>
                )}

                {shouldRenderPrimaryAddToCart && (
                    <div className='headless-add-to-cart'>
                        <button
                            className={`${styleOptions?.buttonClass ? styleOptions.buttonClass + ' btn-sm' : 'headless-button headless-cart-button'}`}
                            onClick={() => handleAddItem.mutate(variables)}
                            disabled={!variables || handleAddItem.isLoading}
                            aria-label={`Add ${product.name} to cart`}
                        >
                            {selectedVariant[pricingType.special] &&
                                selectedVariant[pricingType.standard] && // Check both prices exist
                                selectedVariant[pricingType.special] < selectedVariant[pricingType.standard] ? (
                                <>
                                    <span aria-hidden="true">
                                        <s className='headless-cart-item-price__strikethrough'>
                                            {formattedPrice(
                                                selectedVariant[pricingType.standard]
                                            )}
                                        </s>{' '}
                                    </span>
                                    <span className='headless-pricing-type-special'>
                                        {formattedPrice( // Already bolded by default usually
                                            selectedVariant[pricingType.special]
                                        )}
                                    </span>
                                </>
                            ) : (
                                selectedVariant[pricingType.standard] !== undefined && ( // Ensure standardPrice is defined
                                    <span>
                                        {formattedPrice(
                                            selectedVariant[pricingType.standard]
                                        )}
                                    </span>
                                )
                            )}
                            <span className='headless-card-button-price' aria-hidden="true">|</span>
                            Add to Cart
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ProductCardv1;
